/**
 * A fila da virada da Global (spec 040), no ambiente que liga o modulo `nelogica-v2`. Cada plano ativo no sistema
 * antigo e uma linha; a tela agrupa por pessoa (ate cinco planos cada) e um administrador decide plano a plano:
 * confere e aprova (a pessoa, o e-mail, a conta e a prova do pagamento) ou marca que nao vem, com o motivo.
 *
 * A tela so mostra e manda (ADR-001): o que conferir, quem ja existe no sistema novo e se a conta esta em uso vem do
 * backend; a aprovacao devolve se o plano ficou ativo ou esperando a conta. Carregar o arquivo e mandar o e-mail da
 * virada nao tem tela, de proposito: ficam na ferramenta `importar-global`.
 *
 * A importacao da Prop (`Importacao.tsx`) e outra fila, com outras regras, e segue igual.
 */
import {
  Abas, Aviso, Botao, BotaoLink, CabecalhoDePagina, Campo, CampoCpf, CampoData, CampoSelecao, CampoTextoLongo, Carregando, Segmentado, GrupoDaFila, IconeAlerta, ItemDoGrupo, LinkInterno, ListaDeEscolha, Pilha, Selo, Vazio,
  emailValido, formatarData, formatarDataHora, formatarReais, hojeEmSaoPaulo, soDigitos, type Fato, type OpcaoDeEscolha, type Tom,
} from "@mtc/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router";
import { erroDe, useApi, usePapel, type EtapaDaGlobal } from "../api";
import { usePlanos } from "./Matriculas";
import { DialogoDeAcao } from "./DialogoDeAcao";

type Status = "PENDENTE" | "APROVADA" | "RECUSADA";
type EstadoDaConta = "BLOCKED" | "ACTIVE_CHALLENGE" | "REQUIRES_REVIEW";
type VendaNaGuru = { codigo: string | null; produto: string | null; oferta: string | null; status: string | null; valor: string | null; moeda: string | null; pago_em: string | null };
type Linha = {
  id: string;
  status: Status;
  motivo: string | null;
  conferir: string[];
  decidida_em: string | null;
  matricula_id: string | null;
  pessoa: { nome: string | null; cpf: string | null; cpf_como_veio: string | null; email: string | null; emails: string[]; telefone: string | null; chave_legado: string };
  ja_no_sistema_novo: { id: string; nome: string } | null;
  plano: { slug: string; nome: string | null };
  comprado_em: string | null;
  conta: { numero: string | null; estado: EstadoDaConta | null; motivo_da_revisao: string | null; saldo: string | null; saldo_em: string | null; da_automacao: boolean; em_uso_por: string | null };
  prova: { fonte: "CHECKOUT_PROPRIO"; referencia: string; pago_em: string | null; valor_centavos: number | null } | null;
  vendas_na_guru: VendaNaGuru[];
};
type Lista = { itens: Linha[]; por_status: Partial<Record<Status, number>> };
type Aprovada = { ok: true; pessoa_id: string; matricula_id: string; status: "ATIVA" | "AGUARDANDO_INICIO" };

const STATUS: Status[] = ["PENDENTE", "APROVADA", "RECUSADA"];
const ABA: Record<Status, string> = { PENDENTE: "Pendentes", APROVADA: "Aprovadas", RECUSADA: "Fora" };
/** As marcas do backend (`conferir`) em linguagem de gente. */
const CONFERIR: Record<string, { rotulo: string; tom: Tom; explica: string }> = {
  SEM_CPF: { rotulo: "Sem CPF", tom: "erro", explica: "O sistema antigo não tem um CPF válido para esta pessoa." },
  SEM_EMAIL: { rotulo: "Sem e-mail", tom: "alerta", explica: "O sistema antigo não tem e-mail: digite um na aprovação, é por ele que o cliente recebe o acesso." },
  SEM_CONTA: { rotulo: "Sem conta no sistema antigo", tom: "info", explica: "Confira na extranet e preencha a conta na aprovação, ou aprove sem conta: o plano fica esperando a conta." },
  CONTA_BLOQUEADA: { rotulo: "Conta bloqueada na corretora", tom: "alerta", explica: "Entra como está, com uma pendência no sino pra decidir o que fazer na corretora." },
  CONTA_EM_REVISAO: { rotulo: "Conta em revisão na corretora", tom: "alerta", explica: "A corretora pediu revisão e o sistema antigo não sabe o número da conta." },
  SEM_PROVA: { rotulo: "Sem prova", tom: "alerta", explica: "Nem pedido pago nem venda na Guru: a aprovação pede o motivo por escrito." },
  PROVA_NA_GURU: { rotulo: "Prova na Guru", tom: "info", explica: "Sem pedido do checkout próprio: escolha a venda da Guru na aprovação." },
};
const marca = (c: string) => CONFERIR[c] ?? { rotulo: c, tom: "neutro" as Tom, explica: "" };
const STATUS_NA_GURU: Record<string, { rotulo: string; tom: Tom }> = {
  approved: { rotulo: "Aprovada", tom: "sucesso" },
  paid: { rotulo: "Paga", tom: "sucesso" },
  completed: { rotulo: "Concluída", tom: "sucesso" },
  refunded: { rotulo: "Reembolsada", tom: "alerta" },
  canceled: { rotulo: "Cancelada", tom: "alerta" },
  cancelled: { rotulo: "Cancelada", tom: "alerta" },
  chargeback: { rotulo: "Contestada", tom: "alerta" },
  waiting_payment: { rotulo: "Aguardando pagamento", tom: "alerta" },
};
const statusNaGuru = (s: string | null) => (s ? (STATUS_NA_GURU[s.toLowerCase()] ?? { rotulo: s, tom: "neutro" as Tom }) : null);

const nomeDoPlano = (l: Linha) => l.plano.nome ?? l.plano.slug;
const nomeDaPessoa = (l: Linha) => l.pessoa.nome ?? "Sem nome no sistema antigo";
const plural = (n: number, um: string, varios: string) => `${n} ${n === 1 ? um : varios}`;
/** Decimal que o sistema antigo guarda como texto ("200.96") em formato brasileiro. So exibicao. */
function decimal(valor: string | null, moeda?: string | null): string | null {
  if (!valor) return null;
  const n = Number(valor);
  if (!Number.isFinite(n)) return valor;
  const numero = n.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (!moeda) return numero;
  return moeda.toUpperCase() === "BRL" ? `R$ ${numero}` : moeda.toUpperCase() === "USD" ? `US$ ${numero}` : `${moeda} ${numero}`;
}
const emailsDa = (l: Linha) => [...new Set([l.pessoa.email, ...l.pessoa.emails].filter((e): e is string => Boolean(e?.trim())))];

function useFila() {
  const { ler } = useApi();
  return useQuery({ queryKey: ["importacao-global"], queryFn: () => ler<Lista>("/v1/admin/importacao-global/linhas") });
}

type Grupo = { chave: string; linhas: Linha[]; outras: Record<Status, number> };

/** As linhas da aba, por pessoa (a chave da pessoa no sistema antigo), em ordem de nome. So apresentacao. */
function agrupar(itens: Linha[], aba: Status, filtro: string): Grupo[] {
  const f = filtro.trim().toLowerCase();
  const d = soDigitos(f);
  const bate = (l: Linha) =>
    !f ||
    nomeDaPessoa(l).toLowerCase().includes(f) ||
    nomeDoPlano(l).toLowerCase().includes(f) ||
    emailsDa(l).some((e) => e.toLowerCase().includes(f)) ||
    (d.length >= 3 && (soDigitos(l.pessoa.cpf ?? "").includes(d) || (l.conta.numero ?? "").includes(d)));
  const porPessoa = new Map<string, Linha[]>();
  for (const l of itens) porPessoa.set(l.pessoa.chave_legado, [...(porPessoa.get(l.pessoa.chave_legado) ?? []), l]);
  const grupos: Grupo[] = [];
  for (const [chave, todas] of porPessoa) {
    const daAba = todas.filter((l) => l.status === aba);
    if (!daAba.length || !todas.some(bate)) continue;
    const outras = { PENDENTE: 0, APROVADA: 0, RECUSADA: 0 };
    for (const l of todas) if (l.status !== aba) outras[l.status]++;
    grupos.push({ chave, linhas: daAba, outras });
  }
  return grupos.sort((a, b) => nomeDaPessoa(a.linhas[0]!).localeCompare(nomeDaPessoa(b.linhas[0]!), "pt-BR"));
}

type Feito = { texto: ReactNode; tipo: "sucesso" | "info"; matricula?: string; pessoa?: string };

// ---------------------------------------------------------------- a fila

export function PaginaImportacaoGlobal() {
  const [params, setParams] = useSearchParams();
  const { pode } = usePapel();
  const fila = useFila();
  const [aprovando, setAprovando] = useState<Linha | null>(null);
  const [recusando, setRecusando] = useState<Linha | null>(null);
  const [feito, setFeito] = useState<Feito>();
  const aba = (STATUS.includes(params.get("aba") as Status) ? params.get("aba") : "PENDENTE") as Status;
  const filtro = params.get("q") ?? "";
  const mudar = (chave: string, v: string, padrao = "") => { const p = new URLSearchParams(params); if (v && v !== padrao) p.set(chave, v); else p.delete(chave); setParams(p, { replace: true }); };
  const grupos = agrupar(fila.data?.itens ?? [], aba, filtro);
  const planos = grupos.reduce((n, g) => n + g.linhas.length, 0);
  const contagem = fila.data?.por_status;
  const decidiu = (f: Feito) => { setFeito(f); window.scrollTo({ top: 0, behavior: "smooth" }); };

  return (
    <Pilha espaco={5}>
      <CabecalhoDePagina
        titulo="Importação"
        descricao="Os planos ativos no sistema antigo, por cliente. Cada plano entra no sistema novo só quando um administrador confere e aprova, ou marca que não vem. Nada é enviado ao cliente agora: o e-mail de acesso sai no dia da virada."
      />
      {feito && (
        <Aviso tipo={feito.tipo}>
          <div className="mtc-linha" style={{ gap: "var(--e-2) var(--e-3)", alignItems: "center" }}>
            <span>{feito.texto}</span>
            {feito.matricula && <LinkInterno href={`/matriculas/${feito.matricula}`}>Ver matrícula</LinkInterno>}
            {feito.pessoa && <LinkInterno href={`/traders/${feito.pessoa}`}>Ver ficha do cliente</LinkInterno>}
          </div>
        </Aviso>
      )}
      <div className="mtc-linha mtc-linha--entre" style={{ gap: "var(--e-4)", alignItems: "flex-end" }}>
        <div style={{ flex: "1 1 360px", minWidth: 0 }}>
          <Abas rotulo="Situação" valor={aba} aoMudar={(v) => mudar("aba", v, "PENDENTE")} abas={STATUS.map((s) => ({ valor: s, rotulo: ABA[s], contagem: contagem?.[s] ?? 0 }))} />
        </div>
        <div style={{ flex: "1 1 260px", maxWidth: 360, minWidth: 0 }}>
          <Campo rotulo="Filtrar" valor={filtro} aoMudar={(v) => mudar("q", v)} placeholder="Nome, CPF, e-mail ou conta" />
        </div>
      </div>
      {fila.isLoading ? <Carregando compacto texto="Buscando a fila" /> : fila.isError ? <Aviso tipo="erro">{erroDe(fila.error)}</Aviso> : !grupos.length ? (
        <Vazio titulo={filtro ? "Nada com esse filtro" : aba === "PENDENTE" ? "Nada pendente" : aba === "APROVADA" ? "Nenhum plano aprovado ainda" : "Nenhum plano fora"}>
          {filtro
            ? "Tente outro nome, CPF, e-mail ou conta."
            : aba === "PENDENTE"
              ? (fila.data?.itens.length ? "Todo plano da fila já foi decidido." : "A fila entra pela ferramenta de importação, com o arquivo extraído do sistema antigo.")
              : aba === "APROVADA" ? "Os planos aprovados aparecem aqui, com o link da matrícula." : "Os planos que não vêm aparecem aqui, com o motivo."}
        </Vazio>
      ) : (
        <Pilha espaco={4}>
          <span className="mtc-texto-2" style={{ fontSize: "var(--t-sm)" }}>{plural(planos, "plano", "planos")} de {plural(grupos.length, "cliente", "clientes")}</span>
          {grupos.map((g) => (
            <CartaoDaPessoa key={g.chave} g={g} aba={aba} podeDecidir={pode("ADMIN")} aoAprovar={setAprovando} aoRecusar={setRecusando} />
          ))}
        </Pilha>
      )}
      <Aprovar
        linha={aprovando}
        aoFechar={() => setAprovando(null)}
        aoConcluir={(l, r, conta) => decidiu({
          tipo: r.status === "ATIVA" ? "sucesso" : "info",
          matricula: r.matricula_id,
          pessoa: r.pessoa_id,
          texto: r.status === "ATIVA"
            ? <>Aprovado: {nomeDoPlano(l)} de {nomeDaPessoa(l)} está ativo{conta ? <>, com a conta <span className="mtc-mono">{conta}</span></> : null}.</>
            : <>Aprovado: {nomeDoPlano(l)} de {nomeDaPessoa(l)} está esperando a conta. Ele aparece no sino como “Criar a conta na corretora”; libere pela ficha da matrícula quando tiver o número.</>,
        })}
      />
      <NaoVem linha={recusando} aoFechar={() => setRecusando(null)} aoConcluir={(l) => decidiu({ tipo: "sucesso", texto: <>{nomeDoPlano(l)} de {nomeDaPessoa(l)} não vem. Fica na aba Fora, com o motivo.</> })} />
    </Pilha>
  );
}

function CartaoDaPessoa({ g, aba, podeDecidir, aoAprovar, aoRecusar }: { g: Grupo; aba: Status; podeDecidir: boolean; aoAprovar: (l: Linha) => void; aoRecusar: (l: Linha) => void }) {
  const p = g.linhas[0]!;
  const nome = nomeDaPessoa(p);
  const semCpf = g.linhas.some((l) => l.conferir.includes("SEM_CPF"));
  const ja = g.linhas.find((l) => l.ja_no_sistema_novo)?.ja_no_sistema_novo ?? null;
  const contato = [emailsDa(p).join(", ") || "sem e-mail", p.pessoa.telefone].filter(Boolean).join(", ");
  const outras = (["PENDENTE", "APROVADA", "RECUSADA"] as Status[]).filter((s) => g.outras[s]).map((s) => plural(g.outras[s], { PENDENTE: "pendente", APROVADA: "aprovado", RECUSADA: "fora" }[s], { PENDENTE: "pendentes", APROVADA: "aprovados", RECUSADA: "fora" }[s]));
  return (
    <GrupoDaFila
      rotulo={nome}
      titulo={nome}
      documento={p.pessoa.cpf ?? (p.pessoa.cpf_como_veio ? `CPF inválido no sistema antigo: ${p.pessoa.cpf_como_veio}` : "Sem CPF no sistema antigo")}
      apoio={contato}
      selos={
        <>
          {g.linhas.length > 1 && <Selo tom="info">{plural(g.linhas.length, "plano", "planos")}</Selo>}
          {semCpf && <Selo tom="erro">Sem CPF</Selo>}
          {ja && <Selo tom="sucesso">Já tem cadastro no sistema novo</Selo>}
          {ja && <BotaoLink href={`/traders/${ja.id}`} variante="fantasma" tamanho="pequeno">Ver ficha do cliente</BotaoLink>}
        </>
      }
      aviso={semCpf && aba === "PENDENTE" ? (
        <Aviso tipo="erro">Sem CPF não há cadastro, então não dá pra aprovar pela fila. Corrija o CPF no sistema antigo (a próxima carga atualiza a linha) ou resolva à mão e marque que não vem, com o motivo.</Aviso>
      ) : ja && aba === "PENDENTE" ? (
        <Aviso tipo="info">Esta pessoa já tem cadastro no sistema novo ({ja.nome}). Os planos aprovados entram nela, sem duplicar.</Aviso>
      ) : undefined}
      rodape={outras.length ? `Também desta pessoa: ${outras.join(", ")}.` : undefined}
    >
      {g.linhas.map((l) => <ItemDoPlano key={l.id} l={l} podeDecidir={podeDecidir && !semCpf} podeRecusar={podeDecidir} aoAprovar={aoAprovar} aoRecusar={aoRecusar} />)}
    </GrupoDaFila>
  );
}

/** O que prova o pagamento, numa linha. */
function resumoDaProva(l: Linha): Fato {
  if (l.prova) return { rotulo: "Prova", valor: "Pedido do checkout próprio", detalhe: [l.prova.valor_centavos != null ? formatarReais(l.prova.valor_centavos) : null, l.prova.pago_em ? `pago em ${formatarData(l.prova.pago_em)}` : null].filter(Boolean).join(", ") || undefined };
  if (l.vendas_na_guru.length) return { rotulo: "Prova", valor: plural(l.vendas_na_guru.length, "venda na Guru", "vendas na Guru"), detalhe: "escolha na aprovação" };
  return { rotulo: "Prova", valor: "Sem prova", detalhe: "a aprovação pede o motivo" };
}

function ItemDoPlano({ l, podeDecidir, podeRecusar, aoAprovar, aoRecusar }: { l: Linha; podeDecidir: boolean; podeRecusar: boolean; aoAprovar: (l: Linha) => void; aoRecusar: (l: Linha) => void }) {
  const plano = nomeDoPlano(l);
  const nome = nomeDaPessoa(l);
  const bloqueada = l.conta.estado === "BLOCKED";
  const fatos: Fato[] = [
    // O estado da conta ja aparece nos selos (ativa, bloqueada, em revisao): aqui so o numero e o saldo.
    { rotulo: "Conta", valor: l.conta.numero ?? "sem conta", mono: Boolean(l.conta.numero), detalhe: l.conta.numero ? "no sistema antigo" : undefined },
    ...(l.conta.saldo ? [{ rotulo: "Saldo", valor: decimal(l.conta.saldo) ?? "", detalhe: l.conta.saldo_em ? `em ${formatarData(l.conta.saldo_em)}, no sistema antigo` : "no sistema antigo" }] : []),
    ...(l.conta.motivo_da_revisao ? [{ rotulo: "Motivo da revisão", valor: l.conta.motivo_da_revisao }] : []),
    // Decidida, a prova que valeu e a da matricula (a fila nao diz qual foi escolhida): a ficha da matricula mostra.
    ...(l.status === "PENDENTE" ? [resumoDaProva(l)] : []),
  ];
  const comprado = [l.comprado_em ? `Comprado em ${formatarData(l.comprado_em)}` : "Sem data de compra", l.conta.da_automacao ? "pela automação" : "anterior à automação"].join(", ");
  if (l.status !== "PENDENTE") {
    const aprovada = l.status === "APROVADA";
    return (
      <ItemDoGrupo
        rotulo={`${plano} de ${nome}`}
        titulo={plano}
        apoio={l.decidida_em ? `${aprovada ? "Aprovado em" : "Fora desde"} ${formatarDataHora(l.decidida_em)}` : comprado}
        marcas={<Selo tom={aprovada ? "sucesso" : "neutro"}>{aprovada ? "Aprovado" : "Fora"}</Selo>}
        fatos={fatos}
        aviso={!aprovada && l.motivo ? <Aviso tipo="info"><strong>Motivo:</strong> {l.motivo}</Aviso> : undefined}
        acoes={aprovada && l.matricula_id ? <BotaoLink href={`/matriculas/${l.matricula_id}`} tamanho="pequeno">Ver matrícula</BotaoLink> : undefined}
      />
    );
  }
  return (
    <ItemDoGrupo
      rotulo={`${plano} de ${nome}`}
      tom={bloqueada || l.conta.em_uso_por ? "alerta" : undefined}
      titulo={plano}
      apoio={comprado}
      marcas={
        <>
          {/* Sem CPF e da pessoa: o cartao dela ja diz, com o porque. */}
          {l.conferir.filter((c) => c !== "SEM_CPF").map((c) => (
            <Selo key={c} tom={marca(c).tom} titulo={`${marca(c).rotulo}. ${marca(c).explica}`} icone={c === "CONTA_BLOQUEADA" ? <IconeAlerta tamanho={14} /> : undefined}>{marca(c).rotulo}</Selo>
          ))}
          {l.conta.estado === "ACTIVE_CHALLENGE" && <Selo tom="sucesso">Ativa na corretora</Selo>}
        </>
      }
      fatos={fatos}
      aviso={l.conta.em_uso_por ? <Aviso tipo="alerta">A conta <span className="mtc-mono">{l.conta.numero}</span> já está com um plano que não encerrou, de {l.conta.em_uso_por}. Confira na extranet antes de aprovar.</Aviso> : undefined}
      acoes={
        <>
          {podeDecidir && <Botao tamanho="pequeno" onClick={() => aoAprovar(l)} aria-label={`Conferir e aprovar ${plano} de ${nome}`}>Conferir e aprovar</Botao>}
          {podeRecusar && <Botao tamanho="pequeno" variante="secundario" onClick={() => aoRecusar(l)} aria-label={`Não vem: ${plano} de ${nome}`}>Não vem</Botao>}
        </>
      }
    />
  );
}

// ---------------------------------------------------------------- aprovar

const OUTRO = "__outro__";
const SEM_PROVA = "__sem__";
type Formulario = { cpf: string; nome: string; email: string; emailOutro: string; telefone: string; conta: string; prova: string; motivo: string; plano: string; etapa: EtapaDaGlobal; etapaDesde: string };
/** As duas etapas da Global (spec 044). Toda compra comeca no Challenge (008, 16). */
const ETAPAS_DA_GLOBAL: { valor: EtapaDaGlobal; rotulo: string }[] = [{ valor: "CHALLENGE", rotulo: "Challenge" }, { valor: "FUNDED", rotulo: "Funded" }];

function formularioDe(l: Linha): Formulario {
  const emails = emailsDa(l);
  return { cpf: l.pessoa.cpf ?? "", nome: l.pessoa.nome ?? "", email: emails[0] ?? OUTRO, emailOutro: "", telefone: l.pessoa.telefone ?? "", conta: l.conta.numero ?? "", prova: l.prova ? `CHECKOUT_PROPRIO:${l.prova.referencia}` : "", motivo: "", plano: l.plano.slug, etapa: "CHALLENGE", etapaDesde: "" };
}

/**
 * Conferir e aprovar um plano: a pessoa (CPF, nome, e-mail, telefone), o plano, a etapa, a conta e a prova. O backend
 * confere tudo de novo e recusa com a frase pronta (prova de outra linha, conta de plano vivo, e-mail de outra pessoa,
 * CPF invalido, plano desativado, etapa no futuro). Plano e etapa vem preenchidos com o que veio (spec 044, 3.6); so
 * vao no pedido se o administrador mudou.
 */
function Aprovar({ linha, aoFechar, aoConcluir }: { linha: Linha | null; aoFechar: () => void; aoConcluir: (l: Linha, r: Aprovada, conta: string | null) => void }) {
  const { ler, postar } = useApi();
  const qc = useQueryClient();
  const catalogo = usePlanos();
  // Aberta, a linha e lida de novo: a conta pode ter passado a ser de outro plano desde que a fila carregou.
  const detalhe = useQuery({ queryKey: ["importacao-global", linha?.id], queryFn: () => ler<Linha>(`/v1/admin/importacao-global/linhas/${linha!.id}`), enabled: Boolean(linha) });
  const l = detalhe.data?.id === linha?.id ? (detalhe.data ?? linha) : linha;
  const [f, setF] = useState<Formulario | null>(null);
  const resposta = useRef<{ r: Aprovada; conta: string | null } | null>(null);
  const emails = l ? emailsDa(l) : [];
  const email = f ? (f.email === OUTRO ? f.emailOutro : f.email).trim() : "";
  const semProva = f?.prova === SEM_PROVA;
  const faltando = !f ? [] : [
    ...(soDigitos(f.cpf).length !== 11 ? ["o CPF"] : []),
    ...(f.nome.trim().length < 3 ? ["o nome"] : []),
    ...(!emailValido(email) ? ["o e-mail"] : []),
    ...(f.etapaDesde && f.etapaDesde > hojeEmSaoPaulo() ? ["um dia da etapa que não seja no futuro"] : []),
    ...(!f.prova ? ["a prova do pagamento"] : semProva && f.motivo.trim().length < 5 ? ["o motivo de aprovar sem prova"] : []),
  ];
  const opcoesDeProva: OpcaoDeEscolha[] = !l ? [] : [
    ...(l.prova ? [{
      valor: `CHECKOUT_PROPRIO:${l.prova.referencia}`,
      titulo: "Pedido do checkout próprio",
      detalhe: [l.prova.referencia, l.prova.valor_centavos != null ? formatarReais(l.prova.valor_centavos) : null, l.prova.pago_em ? `pago em ${formatarData(l.prova.pago_em)}` : null].filter(Boolean).join(", "),
      selo: <Selo tom="sucesso">Pago</Selo>,
    }] : []),
    ...l.vendas_na_guru.map((v, i) => {
      const st = statusNaGuru(v.status);
      return {
        valor: v.codigo ? `GURU:${v.codigo}` : `GURU-SEM-CODIGO:${i}`,
        titulo: `Venda na Guru${v.produto ? `: ${v.produto}` : ""}`,
        detalhe: [v.codigo, v.oferta, decimal(v.valor, v.moeda), v.pago_em ? `em ${formatarData(v.pago_em)}` : null].filter(Boolean).join(", "),
        selo: st ? <Selo tom={st.tom}>{st.rotulo}</Selo> : undefined,
        desabilitada: !v.codigo,
        motivo: "Sem o código da transação, esta venda não serve de prova.",
      };
    }),
    { valor: SEM_PROVA, titulo: "Sem prova", detalhe: "Aprovar sem venda que prove o pagamento. Pede o motivo, que fica na trilha." },
  ];
  const corpo = () => {
    const [fonte, ...ref] = f!.prova.split(":");
    return {
      cpf: f!.cpf,
      nome: f!.nome.trim(),
      email,
      telefone: f!.telefone.trim() || null,
      conta: f!.conta.trim() || null,
      prova: semProva ? null : { fonte, referencia: ref.join(":") },
      motivo_sem_prova: semProva ? f!.motivo.trim() : null,
      // Spec 044: so o que o administrador mudou. Challenge sem dia e o que vale sem registro (desde a ativacao).
      ...(f!.plano !== l!.plano.slug ? { plano_slug: f!.plano } : {}),
      ...(f!.etapa !== "CHALLENGE" || f!.etapaDesde ? { etapa: f!.etapa, ...(f!.etapaDesde ? { etapa_desde: f!.etapaDesde } : {}) } : {}),
    };
  };
  // Os planos a venda; o da linha entra mesmo desativado, porque e o que veio do legado.
  const ativos = (catalogo.data?.itens ?? []).filter((p) => p.ativo).map((p) => ({ valor: p.slug, rotulo: p.nome }));
  const opcoesDePlano = l && !ativos.some((p) => p.valor === l.plano.slug) ? [{ valor: l.plano.slug, rotulo: nomeDoPlano(l) }, ...ativos] : ativos;
  const contaMudou = Boolean(l?.conta.numero && f && f.conta.trim() !== l.conta.numero);
  return (
    <DialogoDeAcao
      aberto={Boolean(linha)}
      aoFechar={aoFechar}
      titulo="Conferir e aprovar"
      descricao={l ? <>{nomeDoPlano(l)} de {nomeDaPessoa(l)}{l.comprado_em ? `, comprado em ${formatarData(l.comprado_em)}` : ""}. Confira na extranet e no sistema antigo antes de aprovar.</> : undefined}
      botao="Aprovar"
      podeEnviar={Boolean(f) && faltando.length === 0}
      aoAbrir={() => { resposta.current = null; setF(linha ? formularioDe(linha) : null); }}
      aoEnviar={async () => {
        const c = corpo();
        resposta.current = { r: await postar<Aprovada>(`/v1/admin/importacao-global/linhas/${l!.id}/aprovar`, c), conta: c.conta };
      }}
      aoConcluir={() => {
        void qc.invalidateQueries({ queryKey: ["importacao-global"] });
        void qc.invalidateQueries({ queryKey: ["avisos"] });
        if (l && resposta.current) aoConcluir(l, resposta.current.r, resposta.current.conta);
      }}
    >
      {l && f && (
        <>
          {l.ja_no_sistema_novo && <Aviso tipo="info">Esta pessoa já tem cadastro no sistema novo ({l.ja_no_sistema_novo.nome}). O plano entra nela; um e-mail novo entra como mais um dela.</Aviso>}
          <div className="mtc-grade" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 220px), 1fr))", gap: "var(--e-4)" }}>
            <CampoCpf rotulo="CPF" valor={f.cpf} aoMudar={(v) => setF({ ...f, cpf: v })} dica={l.pessoa.cpf ? "Veio do sistema antigo." : "O sistema antigo não tem CPF válido: confirme com o cliente."} />
            <Campo rotulo="Nome completo" valor={f.nome} aoMudar={(v) => setF({ ...f, nome: v })} autoComplete="off" />
            <Campo rotulo="Telefone" valor={f.telefone} aoMudar={(v) => setF({ ...f, telefone: v })} mono inputMode="tel" autoComplete="off" />
          </div>
          {emails.length ? (
            <ListaDeEscolha
              rotulo="E-mail de acesso"
              nome={`email-${l.id}`}
              valor={f.email}
              aoMudar={(v) => setF({ ...f, email: v })}
              dica="É por ele que o cliente recebe o acesso no dia da virada."
              opcoes={[...emails.map((e) => ({ valor: e, titulo: <span style={{ overflowWrap: "anywhere" }}>{e}</span>, detalhe: e === l.pessoa.email ? "o principal no sistema antigo" : undefined })), { valor: OUTRO, titulo: "Outro e-mail" }]}
            />
          ) : null}
          {(f.email === OUTRO || !emails.length) && (
            <Campo rotulo={emails.length ? "Outro e-mail" : "E-mail de acesso"} type="email" valor={f.emailOutro} aoMudar={(v) => setF({ ...f, emailOutro: v })} autoComplete="off" dica={emails.length ? undefined : "O sistema antigo não tem e-mail. É por ele que o cliente recebe o acesso."} />
          )}
          <CampoSelecao rotulo="Plano" valor={f.plano} aoMudar={(v) => setF({ ...f, plano: v })} opcoes={opcoesDePlano} dica={f.plano !== l.plano.slug ? `No sistema antigo era ${nomeDoPlano(l)}.` : "Veio do sistema antigo. Troque se o sistema antigo gravou errado."} />
          <div className="mtc-grade" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 220px), 1fr))", gap: "var(--e-4)", alignItems: "start" }}>
            <Segmentado<EtapaDaGlobal> rotulo="Etapa" nome={`etapa-${l.id}`} valor={f.etapa} aoMudar={(v) => setF({ ...f, etapa: v })} opcoes={ETAPAS_DA_GLOBAL} />
            <CampoData rotulo="Na etapa desde" valor={f.etapaDesde} aoMudar={(v) => setF({ ...f, etapaDesde: v })} max={hojeEmSaoPaulo()} dica={f.etapa === "CHALLENGE" ? "Em branco, desde a ativação." : "Em branco, desde hoje."} />
          </div>
          <Campo
            rotulo="Conta na corretora"
            valor={f.conta}
            aoMudar={(v) => setF({ ...f, conta: soDigitos(v) })}
            inputMode="numeric"
            mono
            autoComplete="off"
            dica={l.conta.numero ? (contaMudou ? `No sistema antigo era ${l.conta.numero}.` : "Veio do sistema antigo. Confira na extranet.") : "O sistema antigo não tem a conta. Confira na extranet."}
          />
          {!f.conta.trim() && <Aviso tipo="info">Sem conta, o plano entra esperando a conta: aparece no sino como “Criar a conta na corretora” e é liberado pela ficha da matrícula, com o número.</Aviso>}
          {f.conta.trim() && f.conta.trim() === l.conta.numero && l.conta.em_uso_por && <Aviso tipo="alerta">A conta <span className="mtc-mono">{l.conta.numero}</span> já está com um plano que não encerrou, de {l.conta.em_uso_por}. O sistema não aprova duas pessoas na mesma conta: confira na extranet e troque o número, ou deixe vazio.</Aviso>}
          {f.conta.trim() && l.conta.estado === "BLOCKED" && <Aviso tipo="alerta">No sistema antigo, esta conta estava bloqueada na corretora. Ela entra como está, com uma pendência no sino pra decidir o que fazer. Nada é desbloqueado nem encerrado.</Aviso>}
          <ListaDeEscolha rotulo="Prova do pagamento" nome={`prova-${l.id}`} valor={f.prova} aoMudar={(v) => setF({ ...f, prova: v })} opcoes={opcoesDeProva} />
          {semProva && <CampoTextoLongo rotulo="Por que aprovar sem prova" valor={f.motivo} aoMudar={(v) => setF({ ...f, motivo: v })} maxLength={500} placeholder="Ex.: plano dado pelo Yago, conferido na extranet." dica="Fica na trilha, com o seu nome." />}
          {faltando.length > 0 && <span className="mtc-texto-2" style={{ fontSize: "var(--t-sm)" }}>Falta {faltando.join(", ")}.</span>}
        </>
      )}
      {linha && !f && <Carregando compacto texto="Abrindo" />}
    </DialogoDeAcao>
  );
}

// ---------------------------------------------------------------- nao vem

function NaoVem({ linha, aoFechar, aoConcluir }: { linha: Linha | null; aoFechar: () => void; aoConcluir: (l: Linha) => void }) {
  const { postar } = useApi();
  const qc = useQueryClient();
  const [motivo, setMotivo] = useState("");
  return (
    <DialogoDeAcao
      aberto={Boolean(linha)}
      aoFechar={aoFechar}
      titulo="Este plano não vem"
      descricao={linha ? <>{nomeDoPlano(linha)} de {nomeDaPessoa(linha)} fica fora do sistema novo. Nada muda no sistema antigo nem na corretora.</> : undefined}
      botao="Confirmar que não vem"
      variante="perigo"
      podeEnviar={motivo.trim().length >= 5}
      aoAbrir={() => setMotivo("")}
      aoEnviar={() => postar(`/v1/admin/importacao-global/linhas/${linha!.id}/recusar`, { motivo: motivo.trim() })}
      aoConcluir={() => { void qc.invalidateQueries({ queryKey: ["importacao-global"] }); if (linha) aoConcluir(linha); }}
    >
      <CampoTextoLongo rotulo="Por que não vem" valor={motivo} aoMudar={setMotivo} maxLength={500} placeholder="Ex.: plano cancelado pelo cliente em setembro, conferido na extranet." dica="Fica na fila e na trilha, com o seu nome." />
    </DialogoDeAcao>
  );
}
